import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { MatchPublic, MatchTeamPublic, TeamPublic, EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { directionsUrl } from "@/lib/directions";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ResponsiveTable, type CardColumn } from "@/components/responsive-table";
import { Pencil, Trash2, Plus, X, ClipboardList, Activity } from "lucide-react";
import { SendMessageButton } from "@/components/send-message-button";
import { OfflineReadBanner } from "@/components/offline/offline-read-banner";

const roleBadge: Record<string, string> = {
  home: "rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  away: "rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  participating:
    "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

export default function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [match, setMatch] = useState<MatchPublic | null>(null);
  const [event, setEvent] = useState<EventPublic | null>(null);
  const [teams, setTeams] = useState<MatchTeamPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add team dialog state
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<TeamPublic[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedRole, setSelectedRole] = useState("home");
  const [addingTeam, setAddingTeam] = useState(false);

  const reload = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .getMatch(id)
      .then((matchRes) => {
        setMatch(matchRes.match);
        const promises: [Promise<{ event: import("@dugads/shared").EventPublic }> | null, Promise<import("@dugads/shared").MatchTeamListResponse>] = [
          matchRes.match.event_id ? api.getEvent(matchRes.match.event_id) : null,
          api.getMatchTeams(id),
        ];
        return Promise.all([promises[0], promises[1]]);
      })
      .then(([eventRes, teamsRes]) => {
        if (eventRes) setEvent(eventRes.event);
        setTeams(teamsRes.teams);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load match",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleDelete() {
    if (!match || !confirm("Are you sure you want to delete this match?"))
      return;
    try {
      await api.deleteMatch(match.id);
      navigate(`/events/${match.event_id}`);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  async function handleRemoveTeam(teamId: string) {
    if (!match) return;
    try {
      await api.removeMatchTeam(match.id, teamId);
      setTeams((prev) => prev.filter((t) => t.team_id !== teamId));
    } catch (e) {
      setError(
        e instanceof api.ApiError ? e.message : "Failed to remove team",
      );
    }
  }

  function openAddTeam() {
    if (!match || !event) return;
    setShowAddTeam(true);
    setSelectedTeamId("");
    setSelectedRole("home");
    // Load school teams, filter out already assigned
    api
      .getTeams(event.school_id)
      .then((res) => {
        const assignedIds = new Set(teams.map((t) => t.team_id));
        setAvailableTeams(
          res.teams.filter((t) => !assignedIds.has(t.id)),
        );
      })
      .catch(() => {});
  }

  async function handleAddTeam() {
    if (!match || !selectedTeamId) return;
    setAddingTeam(true);
    try {
      const res = await api.addMatchTeam(match.id, {
        team_id: selectedTeamId,
        role: selectedRole as "home" | "away" | "participating",
      });
      setTeams((prev) => [...prev, res.team]);
      setShowAddTeam(false);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to add team");
    } finally {
      setAddingTeam(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="match-detail" data-state="loading">Loading...</p>;
  }

  if (error && !match) {
    return <p className="text-sm text-destructive" data-page="match-detail" data-state="error" data-error>{error}</p>;
  }

  if (!match) {
    return <p className="text-muted-foreground" data-page="match-detail" data-state="empty">Match not found</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="match-detail" data-state={error ? "error" : "ready"}>
      <OfflineReadBanner />
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          {/* 203 -- breadcrumb is desktop-only (198 pattern): the mobile shell
              top bar already provides Back, and this crumb wraps at 390px. */}
          <div className="hidden items-center gap-2 text-sm text-muted-foreground md:flex">
            {event && (
              <>
                <Link
                  to={`/schools/${event.school_id}`}
                  className="hover:underline"
                >
                  {event.school_name}
                </Link>
                <span>/</span>
                <Link
                  to={`/schools/${event.school_id}/events`}
                  className="hover:underline"
                >
                  Events
                </Link>
                <span>/</span>
                <Link
                  to={`/events/${match.event_id}`}
                  className="hover:underline"
                >
                  {event.name}
                </Link>
                <span>/</span>
              </>
            )}
            <span>{match.name}</span>
          </div>
          <h1 className="text-2xl font-semibold">{match.name}</h1>
          <div className="flex items-center gap-3 text-muted-foreground">
            {match.court_name && <span>{match.court_name}</span>}
            {match.age_group && (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {match.age_group}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {match._actions.includes("send_message") && (
            <SendMessageButton
              contextType="match"
              contextId={match.id}
              contextLabel={match.name}
              variant="outline"
              showLabel
            />
          )}
          {match._actions.includes("score_live") && (
            <Link
              to={
                match.sport_id === "NET"
                  ? `/netball/matches/${match.id}/score`
                  : match.sport_id === "RUG7" || match.sport_id === "RUG10"
                    ? `/rugby-sevens/matches/${match.id}/score`
                    : `/rugby/matches/${match.id}/score`
              }
              className={buttonVariants({ variant: "default", size: "sm" })}
              data-action="score-live"
            >
              <Activity className="mr-2 h-4 w-4" aria-hidden />
              Score live
            </Link>
          )}
          {match._actions.includes("edit") && (
            <Link
              to={`/matches/${match.id}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          )}
          {match._actions.includes("delete") && (
            <Button variant="outline" size="sm" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4 text-destructive" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {/* Match Info */}
      <Card data-region="info">
        <CardHeader>
          <CardTitle className="text-base">Match Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Start Time
              </dt>
              <dd className="mt-1">
                {new Date(match.starts_at).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                End Time
              </dt>
              <dd className="mt-1">
                {new Date(match.ends_at).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Court
              </dt>
              <dd className="mt-1">{match.court_name ?? "No court assigned"}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Venue
              </dt>
              <dd className="mt-1">
                {match.venue ? (
                  <div className="flex flex-col gap-1">
                    <span>{match.venue.display_name}</span>
                    {match.venue.address && (
                      <span className="text-xs text-muted-foreground">{match.venue.address}</span>
                    )}
                    {(match.venue.google_place_id ||
                      (match.venue.latitude != null && match.venue.longitude != null) ||
                      match.venue.address) && (
                      <a
                        href={directionsUrl(match.venue)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline text-xs"
                        data-action="get-directions"
                      >
                        Get directions →
                      </a>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">No venue assigned</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Age Group
              </dt>
              <dd className="mt-1">{match.age_group ?? "--"}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Teams Section */}
      <div className="flex flex-col gap-4" data-region="teams">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">
            Teams ({teams.length})
          </h2>
          {match._actions.includes("manage_teams") && (
            <Button size="sm" variant="outline" onClick={openAddTeam}>
              <Plus className="mr-2 h-4 w-4" />
              Add Team
            </Button>
          )}
        </div>

        {teams.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8">
            <p className="text-muted-foreground">No teams assigned yet</p>
          </div>
        )}

        {/* 203 -- one columns array drives the sm+ table AND the <sm cards
            (185 ResponsiveTable), so the phone stops horizontal-scrolling. */}
        {teams.length > 0 && (() => {
          const columns: CardColumn<MatchTeamPublic>[] = [
            { key: "team", header: "Team", cardRole: "title", className: "font-medium", render: (t) => t.team_name },
            { key: "age_group", header: "Age Group", className: "text-muted-foreground", render: (t) => t.age_group },
            { key: "role", header: "Role", render: (t) => <span className={roleBadge[t.role] ?? ""}>{t.role}</span> },
            {
              key: "lineup",
              header: "Lineup",
              cardRole: "action",
              render: (t) => (
                <Link
                  to={`/matches/${match.id}/teams/${t.team_id}/lineup`}
                  className={buttonVariants({ variant: "outline", size: "sm" })}
                >
                  <ClipboardList className="mr-1 h-3.5 w-3.5" />
                  {match._actions.includes("edit") ? "Edit" : "View"}
                </Link>
              ),
            },
            {
              key: "actions",
              header: "",
              cardRole: "action",
              headerClassName: "w-20",
              render: (t) => (
                <div className="flex gap-1">
                  {match._actions.includes("send_message") && (
                    <SendMessageButton contextType="team" contextId={t.team_id} contextLabel={t.team_name} />
                  )}
                  {match._actions.includes("manage_teams") && (
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveTeam(t.team_id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ),
            },
          ];
          return (
            <ResponsiveTable
              columns={columns}
              rows={teams}
              rowKey={(t) => t.team_id}
              rowProps={(t) => ({ "data-match-team-id": t.team_id })}
            />
          );
        })()}
      </div>

      {/* Add Team Dialog */}
      {showAddTeam && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setShowAddTeam(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-background shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-lg font-semibold">Add Team</h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowAddTeam(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
              {availableTeams.length === 0 && (
                <p className="text-muted-foreground">
                  No more teams available to add
                </p>
              )}
              {availableTeams.length > 0 && (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Team</label>
                    <select
                      value={selectedTeamId}
                      onChange={(e) => setSelectedTeamId(e.target.value)}
                      className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">Select a team...</option>
                      {availableTeams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.age_group})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Role</label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="home">Home</option>
                      <option value="away">Away</option>
                      <option value="participating">Participating</option>
                    </select>
                  </div>
                  <Button
                    onClick={handleAddTeam}
                    disabled={!selectedTeamId || addingTeam}
                  >
                    {addingTeam ? "Adding..." : "Add Team"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
